import React, { useState, useEffect, useRef, Fragment } from 'react';
import ErrorSpan from "./ErrorSpan";
import FormGroupHeader from "./FormGroupHeader";
import FormGroupInput from "./FormGroupInput";
import SvgIcon from "../images/SvgIcon";
import { goToEndOfInput } from "../../modules/utils";
import { trackEvent } from "../../modules/tracking";
export default function TypeAhead({ error, label, placeholder, lookupFunction, onSelection, disabled, children, throttleMs = 500, trackingEvent }) {
    const [loading, setLoading] = useState(false);
    const [typedValue, setTypedValue] = useState('');
    const [results, setResults] = useState([]);
    const [lookupError, setLookupError] = useState(null); // Storing these in refs (rather than useState) means clearTimeout/staleness
    // checks always read the latest value, even if a fast burst of keystrokes
    // (e.g. Selenium's sendKeys, or a client-side SPA transition landing on this
    // form before React has settled) causes a stale-closure race with the state
    // setter: a debounced lookup we thought we'd cancelled/superseded could
    // otherwise still land and overwrite newer results with older ones.
    const timeoutRef = useRef(null);
    const requestIdRef = useRef(0);
    const runSearch = e => {
        ;
        e.persist?.();
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (e.target.value.length >= 2) {
            setLoading(true);
            const requestId = ++requestIdRef.current;
            timeoutRef.current = setTimeout(() => {
                lookupFunction(e.target.value).then(data => {
                    // earlier lookup resolving after a faster, more recent one).
                    if (requestId !== requestIdRef.current)
                        return;
                    setResults(data.results);
                    setLoading(false);
                    if (trackingEvent) {
                        trackEvent(trackingEvent, { query: e.target.value, results: data.results?.length ?? 0 });
                    }
                }).catch(error => { if (requestId !== requestIdRef.current)
                    return; if (typeof error === 'string') {
                    setLookupError(error);
                    setLoading(false);
                } console.error(error); });
            }, throttleMs);
        }
        else if (e.target.value.length < 2) {
            setLoading(false);
        }
        setResults([]);
        setTypedValue(e.target.value);
    };
    useEffect(() => {
        return () => { if (timeoutRef.current)
            clearTimeout(timeoutRef.current); }; // Ensure any active timeouts are cleared if the component unmounts.
    }, []);
    const clearInput = e => { if (e) {
        e.preventDefault();
    } setResults([]); setTypedValue(''); };
    const incrementSelected = (value = 1) => { const noOfResults = results ? results.length : 0; let nextFocusKey = (currentFocusKey() + value) % (noOfResults + 1); if (nextFocusKey < 0) {
        nextFocusKey = noOfResults;
    } const focusElement = document.querySelector(`[focuskey="${nextFocusKey}"]`); focusElement?.focus(); };
    const inputFocus = e => { goToEndOfInput(e.target); setTypedValue(e.target.value); if (!e.target.value) {
        setResults([]);
    } };
    const focusOnHover = e => { ; e.target.closest('.search-results__item')?.focus(); };
    const currentFocusKey = () => {
        let activeElement = document.activeElement;
        if (activeElement?.getAttribute('focuskey') === null) {
            activeElement = activeElement?.parentElement || null;
        }
        return parseInt(activeElement?.getAttribute('focuskey') || '0');
    };
    const resultSelected = () => { const currentFocus = currentFocusKey(); const selectedResult = results[currentFocus - 1]; if (currentFocus <= 0) {
        return;
    } setResults([]); setTypedValue(''); onSelection(selectedResult); };
    const handleKeyDown = e => { const code = e.keyCode; switch (code) {
        case 27: return clearInput();
        case 38:
            e.preventDefault();
            return incrementSelected(-1);
        case 40:
            e.preventDefault();
            return incrementSelected();
        case 13:
            e.preventDefault();
            return resultSelected();
    } };
    const resultHeading = name => { const match = name.toLowerCase().match(typedValue.toLowerCase()); let beforeMatch = name; let afterMatch = null; let matchedSpan = null; if (match && match.index !== undefined) {
        const index = match.index;
        beforeMatch = name.substring(0, index);
        afterMatch = name.substring(index + typedValue.length, name.length);
        matchedSpan = /*#__PURE__*/ React.createElement("span", { className: "search-results__heading__matched" }, name.substring(index, index + typedValue.length));
    } return /*#__PURE__*/ React.createElement("span", { className: "search-results__heading" }, beforeMatch, matchedSpan, afterMatch); };
    const inputClasses = loading ? 'string search__input search__input--loading' : 'string search__input';
    return /*#__PURE__*/ React.createElement(Fragment, null, /*#__PURE__*/ React.createElement(FormGroupHeader, { label: label }), /*#__PURE__*/ React.createElement("div", { className: "search", onKeyDown: handleKeyDown }, /*#__PURE__*/ React.createElement(FormGroupInput, { className: inputClasses, type: "text", value: typedValue, autoComplete: "none", autoFocus: true, onChange: runSearch, onFocus: inputFocus, disabled: disabled, placeholder: placeholder, focuskey: '0' }), !disabled && typedValue && /*#__PURE__*/ React.createElement("div", { className: "search__action" }, /*#__PURE__*/ React.createElement("button", { className: "search__button", onClick: clearInput }, /*#__PURE__*/ React.createElement("span", { className: "search__icon" }, /*#__PURE__*/ React.createElement(SvgIcon, { name: "cross", alt: "Clear" })))), results && results.length && /*#__PURE__*/ React.createElement("div", { className: "search-results" }, results.map((result, index) => { const detail = [result.first_line, result.town, result.postcode, result.country, result.trading_name].filter(Boolean).join(', '); return /*#__PURE__*/ React.createElement("div", { className: "search-results__item", key: index + 1, tabIndex: 0, onMouseOver: focusOnHover, onClick: resultSelected, focuskey: index + 1 }, resultHeading(result.name || result.first_line), /*#__PURE__*/ React.createElement("span", { className: "search-results__detail" }, detail)); })) || null), /*#__PURE__*/ React.createElement(ErrorSpan, { error: error || lookupError }), children);
}
